import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

/** Palette tone keys for staff attendance / leave calendar grid cells. */
export type StaffCalendarStatusTone =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "primary"
  | "muted";

export type StaffCalendarGridMode = "attendance" | "leave";

export type StaffCalendarStatusId =
  | "present"
  | "incomplete"
  | "absent"
  | "approvedLeave"
  | "pendingLeave"
  | "offDay"
  | "holiday";

export type StaffCalendarStatusToken = {
  id: StaffCalendarStatusId;
  label: string;
  tone: StaffCalendarStatusTone;
  /** Compact grid symbol per surface (attendance vs leave month grid). */
  symbols: Record<StaffCalendarGridMode, string>;
  fontWeight?: number;
};

/** Canonical symbols + palette tones for attendance and leave calendar grids. */
export const STAFF_CALENDAR_STATUS_TOKENS: StaffCalendarStatusToken[] = [
  {
    id: "present",
    label: "Present (full in/out)",
    tone: "success",
    symbols: { attendance: "P", leave: "P" },
  },
  {
    id: "incomplete",
    label: "Incomplete (single punch)",
    tone: "info",
    symbols: { attendance: "!", leave: "!" },
    fontWeight: 600,
  },
  {
    id: "absent",
    label: "Absent on a working day",
    tone: "error",
    symbols: { attendance: "X", leave: "X" },
    fontWeight: 600,
  },
  {
    id: "approvedLeave",
    label: "Approved leave",
    tone: "primary",
    symbols: { attendance: "L", leave: "A" },
    fontWeight: 600,
  },
  {
    id: "pendingLeave",
    label: "Pending leave",
    tone: "warning",
    symbols: { attendance: "Lp", leave: "Lp" },
    fontWeight: 600,
  },
  {
    id: "offDay",
    label: "Off day (not penalized)",
    tone: "muted",
    symbols: { attendance: "·", leave: "·" },
  },
  {
    id: "holiday",
    label: "Office holiday",
    tone: "muted",
    symbols: { attendance: "H", leave: "H" },
  },
];

const TOKEN_BY_ID = new Map(STAFF_CALENDAR_STATUS_TOKENS.map((t) => [t.id, t]));

export function staffCalendarStatusToken(id: StaffCalendarStatusId): StaffCalendarStatusToken {
  const token = TOKEN_BY_ID.get(id);
  if (!token) throw new Error(`Unknown staff calendar status: ${id}`);
  return token;
}

export function staffCalendarStatusSymbol(
  id: StaffCalendarStatusId,
  mode: StaffCalendarGridMode = "attendance"
): string {
  return staffCalendarStatusToken(id).symbols[mode];
}

export type StaffCalendarLegendItem = {
  id: StaffCalendarStatusId;
  symbol: string;
  label: string;
  tone: StaffCalendarStatusTone;
};

/** Legend rows for a grid mode; optionally hide pending for portal-only viewers. */
export function staffCalendarLegendItems(
  mode: StaffCalendarGridMode,
  opts?: { showPendingLeave?: boolean }
): StaffCalendarLegendItem[] {
  const showPending = opts?.showPendingLeave !== false;
  return STAFF_CALENDAR_STATUS_TOKENS.filter((token) => showPending || token.id !== "pendingLeave")
    .filter((token) => {
      if (mode === "leave") {
        return token.id === "approvedLeave" || token.id === "pendingLeave";
      }
      return true;
    })
    .map((token) => ({
      id: token.id,
      symbol: token.symbols[mode],
      label: token.label,
      tone: token.tone,
    }));
}

export type StaffCalendarCellTone = {
  bgcolor?: string;
  color?: string;
  fontWeight?: number;
};

/**
 * Soft mute for OFF days — light wash + secondary text (not heavy disabled fill).
 * Prefer this over `action.disabledBackground`, which reads too dark in dense grids.
 */
export function staffCalendarOffDaySx(theme: Theme): StaffCalendarCellTone {
  return {
    bgcolor: alpha(theme.palette.divider, 0.35),
    color: theme.palette.text.secondary,
  };
}

/** Swatch / cell styles from a palette tone key (no raw hex). */
export function staffCalendarToneSx(
  theme: Theme,
  tone: StaffCalendarStatusTone
): StaffCalendarCellTone {
  switch (tone) {
    case "success":
      return {
        bgcolor: alpha(theme.palette.success.main, 0.12),
        color: theme.palette.success.dark,
      };
    case "warning":
      return {
        bgcolor: alpha(theme.palette.warning.main, 0.18),
        color: theme.palette.warning.dark,
      };
    case "error":
      return {
        bgcolor: alpha(theme.palette.error.main, 0.12),
        color: theme.palette.error.dark,
      };
    case "info":
      return {
        bgcolor: alpha(theme.palette.info.main, 0.14),
        color: theme.palette.info.dark,
      };
    case "primary":
      return {
        bgcolor: alpha(theme.palette.primary.main, 0.14),
        color: theme.palette.primary.dark,
      };
    default:
      return staffCalendarOffDaySx(theme);
  }
}

/** Grid cell styles for a canonical status id. */
export function staffCalendarStatusSx(
  theme: Theme,
  id: StaffCalendarStatusId,
  opts?: { fontWeight?: number }
): StaffCalendarCellTone {
  const token = staffCalendarStatusToken(id);
  const tone = staffCalendarToneSx(theme, token.tone);
  const fontWeight = opts?.fontWeight ?? token.fontWeight;
  return fontWeight != null ? { ...tone, fontWeight } : tone;
}
